import axios from "axios";
import AddSite from "Components/Admin-Components/Site/AddSite";
import EditSite from "Components/Admin-Components/Site/EditSite";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaSearch } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { TiEdit } from "react-icons/ti";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { TbFileDownload } from "react-icons/tb";
import { IoIosArrowDropleft, IoIosArrowDropright } from "react-icons/io";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ConfirmDeleteModal from "Components/Admin-Components/Common Modals/ConfirmDeleteModal";

function Site() {
  //States for opening different modals
  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const [editSiteModalOpen, setEditSiteModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [loading, setLoading] = useState(false); //State for loader
  const [allSites, setAllSites] = useState([]); //State for storing all sites
  const [searchTerm, setSearchTerm] = useState(""); //State for storing the search term
  const [selectedSite, setSelectedSite] = useState({}); //State for storing a selected site
  const [filteredSites, setFilteredSites] = useState([]); //State for storing filtered sites

  //State for pagination
  const [currentPage, setCurrentPage] = useState(1);

  //Fetching user details from the local storage
  const storedData = localStorage.getItem("Ananya Enterprize-Users-Data");

  //Converting the fetched user details into JSON
  const userData = storedData ? JSON.parse(storedData) : {};

  //Function to get all the sites
  const getAllSites = () => {
    setLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/site/get-all-sites`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of getAllSites: ", response.data);
        setAllSites(response.data.sites);
        setLoading(false);
      })
      .catch((error) => {
        console.log("Error in fetching all sites: ", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllSites();
  }, []);

  //Function to delete a site
  const deleteSite = (siteId) => {
    setLoading(true);
    let config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/site/delete-site/${siteId}`,
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of deleteSite: ", response.data);
        setLoading(false);
        setDeleteModalOpen(false);
        getAllSites();
        toast.success(response.data.message);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        toast.error("Error in deleting site");
      });
  };

  //Function to generate and download site report in PDF
  const generatePDF = (site) => {
    const pending = site?.tasks?.filter((task) => task.status === "Pending");
    const completed = site?.tasks?.filter(
      (task) => task.status === "Completed"
    );
    const doc = new jsPDF();
    // Set Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const title = "Site Report";
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, 20); // Centered title

    // Staff Name Section
    if (site?.siteName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(14);
      doc.text(`Site Name : ${site?.siteName}`, 14, 30);
    }

    let currentY = 40; // Dynamic positioning

    // Pending Tasks Table
    const isAdmin = userData?.role === "admin";
    const pendingHeaders = isAdmin
      ? ["Sl", "Name", "Category", "Location", "Start Date", "Staff"]
      : ["Sl", "Name", "Category", "Location", "Start Date"];

    const pendingBody = pending?.map((task, index) => {
      const row = [
        index + 1,
        task?.name,
        task?.category?.name,
        task?.location,
        new Date(task?.startDate)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"),
      ];
      if (isAdmin) {
        row.push(task.staffAssigned?.name || "N/A");
      }
      return row;
    });

    if (pending?.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text(`Pending Tasks - ${pending?.length}`, 14, currentY);
      doc.setFont("helvetica", "normal");

      doc.autoTable({
        startY: currentY + 5,
        head: [pendingHeaders],
        body: pendingBody,
        theme: "grid", // Adds border around table
        styles: { fontSize: 12, cellPadding: 3 },
        headStyles: {
          fillColor: [100, 149, 237],
          textColor: 233,
          fontStyle: "bold",
        }, //Blue Header
        alternateRowStyles: { fillColor: [240, 240, 240] }, // Gray Stripe
      });

      currentY = doc.autoTable.previous.finalY + 10;
    }

    // Completed Tasks Table
    if (completed?.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text(`Completed Tasks - ${completed?.length}`, 14, currentY);
      doc.setFont("helvetica", "normal");

      doc.autoTable({
        startY: currentY + 5,
        head: [["Sl", "Name", "Category", "Location", "Start Date"]],
        body: completed?.map((task, index) => [
          index + 1,
          task?.name,
          task?.category,
          task?.location,
          new Date(task?.startDate)
            .toLocaleDateString("en-GB")
            .replace(/\//g, "-"),
        ]),
        theme: "grid",
        styles: { fontSize: 12, cellPadding: 3 },
        headStyles: {
          fillColor: [34, 139, 34],
          textColor: 255,
          fontStyle: "bold",
        }, // Green Header
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
    }

    // Save PDF
    doc.save(`${site?.siteName}-Site Report.pdf`);
  };

  const handleActionButtonClick = (clickType, site) => {
    setSelectedSite(site);
    if (clickType === "Edit") {
      setEditSiteModalOpen(true);
    } else if (clickType === "Delete") {
      setDeleteModalOpen(true);
    }
  };

  //Searching sites by siteName, district
  useEffect(() => {
    const filteredSites = allSites?.filter((site) => {
      const matchesSearch =
        site?.siteName
          ?.toLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) ||
        site?.district?.toLowerCase().includes(searchTerm.toLocaleLowerCase());

      return matchesSearch;
    });
    setFilteredSites(filteredSites);
  }, [searchTerm, allSites]);

  //Pagination Logic
  const sitesPerPage = 10;
  const totalPages = Math.ceil(allSites?.length / sitesPerPage);

  //Function to handle page change
  const handlePageChange = (newPage) => {
    if (newPage === 0) {
      setCurrentPage(1);
    } else if (newPage > totalPages) {
      setCurrentPage(totalPages);
    } else {
      setCurrentPage(newPage);
    }
  };

  //Finding the sites for the current page
  const currentSites = filteredSites?.slice(
    (currentPage - 1) * sitesPerPage,
    currentPage * sitesPerPage
  );

  return (
    <div className="mx-2 my-6 md:my-8">
      {/* All Modals are rendered here */}

      {/* Add Site Modal  */}
      {addSiteModalOpen && (
        <AddSite
          userId={userData._id}
          onClose={() => setAddSiteModalOpen(false)}
          getAllSites={getAllSites}
        />
      )}

      {/* Edit Site Modal  */}
      {editSiteModalOpen && (
        <EditSite
          site={selectedSite}
          onClose={() => setEditSiteModalOpen(false)}
          userId={userData._id}
          getAllSites={getAllSites}
        />
      )}

      {/* Delete Confirmation Modal  */}
      {deleteModalOpen && (
        <ConfirmDeleteModal
          item="site"
          deleteFunction={() => deleteSite(selectedSite._id)}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-wide text-primary ">
          Sites
        </h1>
        <button
          className="flex h-fit items-center gap-1 rounded bg-primary px-2 py-1 text-sm font-bold tracking-wide text-white hover:bg-hoverPrimary"
          onClick={() => setAddSiteModalOpen(true)}
        >
          Add Site <FaLocationDot />
        </button>
      </div>

      <div className="my-6 flex flex-col gap-4">
        {/* Search bar and other filters  */}
        <div className="relative flex items-center justify-between">
          <input
            type="text"
            placeholder="Search..."
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[100%] rounded-lg border border-gray-300 p-2 pl-8 pr-10 text-primaryText focus:outline-none focus:ring-1 focus:ring-blue-400 lg:w-auto"
          />
          <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 transform text-primary" />
        </div>

        {/* Site table goes here  */}
        <div className="min-h-[70vh] overflow-x-auto ">
          <table className="min-w-full overflow-y-visible rounded-md border-blue-200 ">
            {/* Table Head  */}
            <thead>
              <tr className="bg-blue-50">
                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Sl
                </th>

                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Site Name
                </th>

                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  District
                </th>

                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Range
                </th>

                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Site Description
                </th>

                <th className="rounded-tl-xl px-6 py-3 text-left text-[14px] font-semibold text-primaryText">
                  Actions
                </th>
              </tr>
            </thead>

            {/* Table Body  */}
            <tbody className="text-[13px] font-medium tracking-wide text-[#202224]">
              {loading ? (
                //Skeleton Loader
                [...Array(10)].map((_, index) => (
                  <tr
                    key={index}
                    className="animate-pulse border-b border-gray-200"
                  >
                    <td className="px-6 py-4">
                      <div className="h-4  w-1/3 rounded bg-skeletonLoaderColor"></div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="h-4  w-3/4 rounded bg-skeletonLoaderColor"></div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="h-4  w-full rounded bg-skeletonLoaderColor"></div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="h-4  w-1/2 rounded bg-skeletonLoaderColor"></div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="h-4  w-1/3 rounded bg-skeletonLoaderColor"></div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="h-4  w-1/2 rounded bg-skeletonLoaderColor"></div>
                    </td>
                  </tr>
                ))
              ) : currentSites.length > 0 ? (
                currentSites.map((site, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {(currentPage - 1) * sitesPerPage + index + 1}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {site.siteName}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {site.district}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {site.rangesFrom || "N/A"}-{site.rangesTo || "N/A"}
                    </td>

                    <td className="relative px-6 py-4  text-sm text-primaryText">
                      {site.siteDescription || "N/A"}
                    </td>

                    <td className="relative flex gap-2 px-6 py-4  text-sm text-primaryText">
                      {userData.role === "admin" && (
                        <div className="flex items-center gap-2">
                          {/* Edit Site Button  */}
                          <button>
                            <TiEdit
                              size={24}
                              title="Edit Site"
                              className="cursor-pointer font-bold text-green-500 duration-300 hover:scale-105 hover:text-green-800"
                              onClick={() =>
                                handleActionButtonClick("Edit", site)
                              }
                            />
                          </button>

                          {/* Delete Site Button */}
                          <button>
                            <MdOutlineDeleteOutline
                              size={24}
                              title="Delete Site"
                              className="cursor-pointer font-bold text-red-500 duration-300 hover:scale-105 hover:text-red-800"
                              onClick={() =>
                                handleActionButtonClick("Delete", site)
                              }
                            />
                          </button>
                        </div>
                      )}
                      {userData?.role === "staff" && (
                        <button>
                          <TbFileDownload
                            size={24}
                            title="Download Site Report"
                            className="cursor-pointer font-bold text-blue-500 duration-300 hover:scale-105 hover:text-blue-800"
                            onClick={() => {
                              if (site?.tasks?.length > 0) {
                                setSelectedSite(site);
                                generatePDF(site);
                              } else {
                                toast.error("No tasks available in this site");
                              }
                            }}
                          />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-40 text-center text-lg text-gray-600"
                  >
                    No sites available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="float-right my-4 flex items-center justify-end gap-2 font-medium">
          <span className="text-sm text-primaryText">
            {currentPage} - {totalPages} pages
          </span>
          <div className="flex  items-center gap-2">
            <button
              className={`cursor-pointer rounded font-bold ${
                currentPage === 1
                  ? "text-gray-300 hover:text-gray-300"
                  : "text-primary hover:text-hoverPrimary"
              }`}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <IoIosArrowDropleft size={24} />
            </button>

            <button
              className={`cursor-pointer rounded font-bold ${
                currentPage === totalPages
                  ? "text-gray-300 hover:text-gray-300"
                  : "text-primary hover:text-hoverPrimary"
              }`}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <IoIosArrowDropright size={24} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Site;
